/** Storyboard / Video Production Format Contract — three explicit layers.
 *
 * 1. STORYBOARD DELIVERY FORMAT — aspect_ratio + delivery resolution
 *    (e.g. 16:9 / 1080p → delivery target 1920×1080). Product UI lock.
 * 2. PROVIDER NATIVE FORMAT — whatever the Image/Video provider actually
 *    generates (e.g. 1344×768). Must be aspect-compatible; never forced
 *    to equal the delivery pixel size.
 * 3. FINAL CONFORM FORMAT — unify to delivery target at final merge /
 *    conform time only. Keyframes must NOT be immediately upscaled to
 *    delivery just to feed the Video Provider.
 *
 * Hard lock for Storyboard-derived keyframes: **aspect_ratio only**.
 * Delivery resolution is advisory metadata for conform — not a native
 * generation size requirement.
 */

export const PRODUCTION_ASPECT_RATIO_OPTIONS = ['16:9', '9:16', '1:1', '4:3', '3:4'] as const
export type ProductionAspectRatio = (typeof PRODUCTION_ASPECT_RATIO_OPTIONS)[number]

export const PRODUCTION_RESOLUTION_OPTIONS = ['720p', '1080p'] as const
export type ProductionResolution = (typeof PRODUCTION_RESOLUTION_OPTIONS)[number]

export interface StoryboardProductionFormat {
  aspect_ratio: ProductionAspectRatio
  /** Delivery / conform resolution tier — NOT provider native size. */
  resolution: ProductionResolution
  /** Monotonic — increments when aspect or delivery resolution changes. */
  version: number
}

export const DEFAULT_PRODUCTION_FORMAT: StoryboardProductionFormat = {
  aspect_ratio: '16:9',
  resolution: '1080p',
  version: 1,
}

const ASPECT_SET = new Set<string>(PRODUCTION_ASPECT_RATIO_OPTIONS)
const RES_SET = new Set<string>(PRODUCTION_RESOLUTION_OPTIONS)

export function normalizeProductionAspectRatio(raw: unknown): ProductionAspectRatio | null {
  const v = String(raw ?? '').trim()
  if (!v || v.toUpperCase() === 'AUTO') return null
  return ASPECT_SET.has(v) ? (v as ProductionAspectRatio) : null
}

export function normalizeProductionResolution(raw: unknown): ProductionResolution | null {
  const v = String(raw ?? '').trim().toLowerCase().replace(/p$/i, 'p')
  const mapped =
    v === '720' || v === '720p'
      ? '720p'
      : v === '1080' || v === '1080p'
        ? '1080p'
        : v
  return RES_SET.has(mapped) ? (mapped as ProductionResolution) : null
}

/** Map contract resolution → Generate Video qualityTier field. */
export function resolutionToQualityTier(resolution: ProductionResolution): '720P' | '1080P' {
  return resolution === '720p' ? '720P' : '1080P'
}

export function qualityTierToResolution(tier: unknown): ProductionResolution | null {
  return normalizeProductionResolution(tier)
}

/**
 * Map Storyboard delivery resolution → Generate Image `quality` field.
 * Quality only affects steps / product tier — NOT pixel dimensions
 * (see backend `image_generation_product_options.RATIO_DIMENSIONS`).
 */
export function resolutionToImageQuality(resolution: ProductionResolution): 'STANDARD' | 'HIGH' {
  return resolution === '1080p' ? 'HIGH' : 'STANDARD'
}

/** Delivery / final-conform pixel target — independent of provider native. */
export interface DeliveryProductionTarget {
  aspect_ratio: ProductionAspectRatio
  resolution: ProductionResolution
  width: number
  height: number
  version: number
}

/** @deprecated Prefer DeliveryProductionTarget — name kept for call-site compat. */
export type CanonicalProductionTarget = DeliveryProductionTarget

/** Resolve DELIVERY target pixels (e.g. 16:9/1080p → 1920×1080). */
export function resolveDeliveryProductionTarget(
  format: StoryboardProductionFormat,
): DeliveryProductionTarget | null {
  if (!isProductionFormatComplete(format)) return null
  const short = format.resolution === '720p' ? 720 : 1080
  const long = format.resolution === '720p' ? 1280 : 1920
  let width = long
  let height = short
  switch (format.aspect_ratio) {
    case '16:9':
      width = long
      height = short
      break
    case '9:16':
      width = short
      height = long
      break
    case '1:1':
      width = short
      height = short
      break
    case '4:3':
      width = Math.round((short * 4) / 3)
      height = short
      break
    case '3:4':
      width = short
      height = Math.round((short * 4) / 3)
      break
    default:
      break
  }
  return {
    aspect_ratio: format.aspect_ratio,
    resolution: format.resolution,
    width,
    height,
    version: format.version,
  }
}

/** @deprecated Alias — delivery target only; never force provider native size. */
export function resolveCanonicalProductionTarget(
  format: StoryboardProductionFormat,
): DeliveryProductionTarget | null {
  return resolveDeliveryProductionTarget(format)
}

export interface ProductionFormatChangeImpact {
  aspectChanged: boolean
  resolutionChanged: boolean
  /** Aspect change → AI keyframe/video source must regenerate. */
  aiGenerationStale: boolean
  /** Delivery resolution-only change → conform/final render stale; AI source OK. */
  deliveryRenderStale: boolean
}

/**
 * Classify format delta for stale policy.
 * CASE: 16:9→9:16 → aiGenerationStale
 * CASE: 1080p→720p with same aspect → deliveryRenderStale only
 */
export function classifyProductionFormatChange(
  previous: StoryboardProductionFormat | null | undefined,
  next: StoryboardProductionFormat | null | undefined,
): ProductionFormatChangeImpact {
  if (!previous || !next || !isProductionFormatComplete(previous) || !isProductionFormatComplete(next)) {
    return {
      aspectChanged: false,
      resolutionChanged: false,
      aiGenerationStale: false,
      deliveryRenderStale: false,
    }
  }
  const aspectChanged = previous.aspect_ratio !== next.aspect_ratio
  const resolutionChanged = previous.resolution !== next.resolution
  return {
    aspectChanged,
    resolutionChanged,
    aiGenerationStale: aspectChanged,
    deliveryRenderStale: resolutionChanged && !aspectChanged,
  }
}

/**
 * Fields stamped onto Shot-bound Generate Image / Generate Video nodes.
 * Locks **aspect ratio** for generation; stamps delivery target metadata
 * for final conform — does NOT require native WxH == delivery WxH.
 */
/** Standalone Generate Video — format is editable; stamp contract only. */
export function buildStandaloneVideoFormatFields(format: StoryboardProductionFormat): Record<string, unknown> {
  const target = resolveDeliveryProductionTarget(format)
  return {
    nodeProductionFormat: format,
    lockedProductionFormat: format,
    ratio: format.aspect_ratio,
    quality: resolutionToImageQuality(format.resolution),
    qualityTier: resolutionToQualityTier(format.resolution),
    deliveryTargetWidth: target?.width ?? null,
    deliveryTargetHeight: target?.height ?? null,
    productionTargetWidth: target?.width ?? null,
    productionTargetHeight: target?.height ?? null,
    productionFormatVersion: format.version,
    aspectRatioLocked: false,
    resolutionLocked: false,
    productionFormatLocked: false,
    formatCompatibility: 'ok',
    deliveryRenderStale: false,
    keyframeImmediateUpscaleRequired: false,
  }
}

/** Storyboard-bound shot / keyframe child — aspect inherited; resolution stays editable. */
export function buildLockedProductionFormatFields(format: StoryboardProductionFormat): Record<string, unknown> {
  const target = resolveDeliveryProductionTarget(format)
  return {
    lockedProductionFormat: format,
    ratio: format.aspect_ratio,
    quality: resolutionToImageQuality(format.resolution),
    qualityTier: resolutionToQualityTier(format.resolution),
    deliveryTargetWidth: target?.width ?? null,
    deliveryTargetHeight: target?.height ?? null,
    productionTargetWidth: target?.width ?? null,
    productionTargetHeight: target?.height ?? null,
    generatedAgainstAspectRatio: format.aspect_ratio,
    generatedAgainstFormatVersion: format.version,
    formatCompatibility: 'ok',
    deliveryRenderStale: false,
    aspectRatioLocked: true,
    resolutionLocked: false,
    productionFormatLocked: false,
    keyframeImmediateUpscaleRequired: false,
  }
}

/** Optional provider-native provenance — never replaces product delivery display. */
export function buildProviderNativeProvenance(args: {
  width?: number | null
  height?: number | null
}): {
  provider_native_width: number | null
  provider_native_height: number | null
  native_width: number | null
  native_height: number | null
} {
  const w = typeof args.width === 'number' ? args.width : null
  const h = typeof args.height === 'number' ? args.height : null
  return {
    provider_native_width: w,
    provider_native_height: h,
    native_width: w,
    native_height: h,
  }
}

/**
 * Image-generation provider-native dimensions for a product aspect ratio
 * (mirrors backend `image_generation_product_options.RATIO_DIMENSIONS`).
 */
export const IMAGE_PROVIDER_NATIVE_RATIO_DIMENSIONS: Record<ProductionAspectRatio, { width: number; height: number }> = {
  '1:1': { width: 1024, height: 1024 },
  '4:3': { width: 1152, height: 864 },
  '3:4': { width: 864, height: 1152 },
  '16:9': { width: 1344, height: 768 },
  '9:16': { width: 768, height: 1344 },
}

export function resolveProviderNativeSizeForAspect(
  aspect: ProductionAspectRatio,
): { width: number; height: number } | null {
  return IMAGE_PROVIDER_NATIVE_RATIO_DIMENSIONS[aspect] ?? null
}

export type KeyframeFormatSource = 'storyboard' | 'video_node' | 'none'

export function resolveKeyframeFormatSource(fields: Record<string, unknown>): KeyframeFormatSource {
  if (fields.boundOrchestratorNodeId || fields.formatSource === 'storyboard') return 'storyboard'
  if (fields.candidateForVideoKeyframe || fields.boundVideoNodeId || fields.formatSource === 'video_node') {
    return 'video_node'
  }
  return 'none'
}

/** Derive format from a Video node (standalone CASE B or locked fields). */
export function deriveProductionFormatFromVideoFields(
  fields: Record<string, unknown>,
): StoryboardProductionFormat | null {
  const fromVideo = readProductionFormatFromVideoFields(fields)
  if (fromVideo) return fromVideo
  const nodeFmt = fields.nodeProductionFormat
  if (nodeFmt && typeof nodeFmt === 'object') {
    return readProductionFormat({ productionFormat: nodeFmt })
  }
  return null
}

/**
 * Fail-fast: provider must support the locked aspect ratio (no silent 1:1).
 */
export function assertProviderSupportsProductionAspect(args: {
  aspectRatio: string
  supportedRatios?: string[] | null
  parameterCapabilities?: Record<string, { supported?: boolean; supported_values?: string[] }> | null
}): { ok: true } | { ok: false; message: string } {
  const aspect = normalizeProductionAspectRatio(args.aspectRatio)
  if (!aspect) {
    return { ok: false, message: '成片规格缺少有效长宽比，无法生成关键帧' }
  }
  const cap = args.parameterCapabilities?.aspect_ratio || args.parameterCapabilities?.ratio
  if (cap && cap.supported === false) {
    return { ok: false, message: `当前图片 Provider 不支持长宽比 ${aspect}，无法满足成片规格` }
  }
  const fromCap = Array.isArray(cap?.supported_values) ? cap!.supported_values! : null
  const list = (fromCap && fromCap.length ? fromCap : args.supportedRatios) || null
  if (list && list.length > 0) {
    const normalized = list.map((r) => String(r).trim())
    if (!normalized.includes(aspect)) {
      return {
        ok: false,
        message: `当前图片 Provider 不支持长宽比 ${aspect}（支持：${normalized.join('、')}），禁止静默降级`,
      }
    }
  }
  if (!resolveProviderNativeSizeForAspect(aspect)) {
    return { ok: false, message: `无法为长宽比 ${aspect} 解析 Provider 原生尺寸` }
  }
  return { ok: true }
}

/** source_context for image-generation tasks used as video keyframes. */
export function buildKeyframeImageSourceContext(args: {
  nodeId: string
  format: StoryboardProductionFormat
  formatSource: KeyframeFormatSource
  videoNodeId?: string | null
  timelineEntryId?: string | null
  orchestratorNodeId?: string | null
  expectedReferenceAssetIds?: string[]
}): Record<string, unknown> {
  const target = resolveDeliveryProductionTarget(args.format)
  const native = resolveProviderNativeSizeForAspect(args.format.aspect_ratio)
  return {
    source: 'production_canvas',
    node_id: args.nodeId,
    purpose: args.formatSource === 'storyboard' ? 'storyboard_shot_keyframe' : 'video_keyframe',
    production_format: {
      aspect_ratio: args.format.aspect_ratio,
      target_resolution: args.format.resolution,
      production_format_version: args.format.version,
    },
    delivery_target_width: target?.width ?? null,
    delivery_target_height: target?.height ?? null,
    canonical_output_width: target?.width ?? null,
    canonical_output_height: target?.height ?? null,
    provider_native_width: native?.width ?? null,
    provider_native_height: native?.height ?? null,
    keyframe_immediate_upscale_required: false,
    expected_reference_asset_ids: args.expectedReferenceAssetIds ?? [],
    format_source: args.formatSource,
    video_node_id: args.videoNodeId ?? null,
    timeline_entry_id: args.timelineEntryId ?? null,
    orchestrator_node_id: args.orchestratorNodeId ?? null,
  }
}

/**
 * Read production format from node fields.
 * Migrates legacy `aspectRatio` (non-AUTO) into a provisional format when
 * `productionFormat` is absent — never invents AUTO as final.
 */
export function readProductionFormat(fields: Record<string, unknown>): StoryboardProductionFormat | null {
  const raw = fields.productionFormat
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    const aspect = normalizeProductionAspectRatio(obj.aspect_ratio ?? obj.aspectRatio)
    const resolution = normalizeProductionResolution(obj.resolution ?? obj.quality_target ?? obj.qualityTier)
    const version = typeof obj.version === 'number' && obj.version > 0 ? Math.floor(obj.version) : 1
    if (aspect && resolution) {
      return { aspect_ratio: aspect, resolution, version }
    }
  }
  const legacyAspect = normalizeProductionAspectRatio(fields.aspectRatio)
  const legacyRes =
    normalizeProductionResolution(fields.productionResolution) ||
    normalizeProductionResolution(fields.qualityTier) ||
    null
  if (legacyAspect && legacyRes) {
    return { aspect_ratio: legacyAspect, resolution: legacyRes, version: 1 }
  }
  return null
}

export function isProductionFormatComplete(format: StoryboardProductionFormat | null | undefined): boolean {
  if (!format) return false
  return Boolean(normalizeProductionAspectRatio(format.aspect_ratio) && normalizeProductionResolution(format.resolution))
}

/** Product-recommended starter (visible preselection) — still explicit values. */
export function recommendedProductionFormat(): StoryboardProductionFormat {
  return { ...DEFAULT_PRODUCTION_FORMAT }
}

export function productionFormatFingerprint(format: StoryboardProductionFormat | null): string {
  if (!format || !isProductionFormatComplete(format)) return 'MISSING'
  return `${format.aspect_ratio}|${format.resolution}|v${format.version}`
}

export function formatsEqualIgnoringVersion(
  a: StoryboardProductionFormat | null | undefined,
  b: StoryboardProductionFormat | null | undefined,
): boolean {
  if (!a || !b) return false
  return a.aspect_ratio === b.aspect_ratio && a.resolution === b.resolution
}

/**
 * Apply a user edit. Returns next format (version bumped when aspect/resolution
 * actually change), whether a bump occurred, and stale impact classification.
 */
export function applyProductionFormatChange(
  current: StoryboardProductionFormat | null,
  patch: { aspect_ratio?: ProductionAspectRatio; resolution?: ProductionResolution },
): {
  next: StoryboardProductionFormat
  versionBumped: boolean
  impact: ProductionFormatChangeImpact
} {
  const base = current && isProductionFormatComplete(current) ? current : recommendedProductionFormat()
  const aspect = patch.aspect_ratio ?? base.aspect_ratio
  const resolution = patch.resolution ?? base.resolution
  const changed = aspect !== base.aspect_ratio || resolution !== base.resolution
  if (!changed && current && isProductionFormatComplete(current)) {
    return {
      next: current,
      versionBumped: false,
      impact: classifyProductionFormatChange(current, current),
    }
  }
  const version = changed && current ? current.version + 1 : current?.version || 1
  const next: StoryboardProductionFormat = {
    aspect_ratio: aspect,
    resolution,
    version: Math.max(1, version),
  }
  const versionBumped = changed && Boolean(current && isProductionFormatComplete(current))
  return {
    next,
    versionBumped,
    impact: versionBumped
      ? classifyProductionFormatChange(current, next)
      : {
          aspectChanged: false,
          resolutionChanged: false,
          aiGenerationStale: false,
          deliveryRenderStale: false,
        },
  }
}

/** True when node has any generated shot video / final merge / bound generator result. */
export function hasGeneratedMediaRequiringFormatConfirm(args: {
  generatedShotVideoCount?: number
  hasFinalVideo?: boolean
  hasGeneratedKeyframes?: boolean
}): boolean {
  return (
    (args.generatedShotVideoCount ?? 0) > 0 ||
    Boolean(args.hasFinalVideo) ||
    Boolean(args.hasGeneratedKeyframes)
  )
}

export function formatMismatchCopy(): { title: string; content: string; okText: string; cancelText: string } {
  return {
    title: '修改成片规格？',
    content:
      '修改画面比例后，当前分镜下已生成的关键帧和视频构图将不再匹配，需要重新生成。仅改交付清晰度时，可保留 AI 源素材并在合片时重新 conform。是否继续？',
    okText: '继续修改',
    cancelText: '取消',
  }
}

/**
 * Resolve Production Format from a Generate Video node's fields.
 * Prefers stamped `lockedProductionFormat`; falls back to ratio + qualityTier
 * (standalone Video shared contract).
 */
export function readProductionFormatFromVideoFields(
  fields: Record<string, unknown>,
): StoryboardProductionFormat | null {
  const locked = fields.lockedProductionFormat
  if (locked && typeof locked === 'object') {
    const obj = locked as Record<string, unknown>
    const aspect = normalizeProductionAspectRatio(obj.aspect_ratio ?? obj.aspectRatio)
    const resolution = normalizeProductionResolution(obj.resolution)
    const version = typeof obj.version === 'number' && obj.version > 0 ? Math.floor(obj.version) : 1
    if (aspect && resolution) return { aspect_ratio: aspect, resolution, version }
  }
  const fromOrch = readProductionFormat(fields)
  if (fromOrch) return fromOrch
  const aspect = normalizeProductionAspectRatio(fields.ratio)
  const resolution =
    qualityTierToResolution(fields.qualityTier) ||
    normalizeProductionResolution(fields.quality) ||
    null
  if (!aspect || !resolution) return null
  const version =
    typeof fields.generatedAgainstFormatVersion === 'number' && fields.generatedAgainstFormatVersion > 0
      ? Math.floor(fields.generatedAgainstFormatVersion as number)
      : typeof fields.production_format_version === 'number' && (fields.production_format_version as number) > 0
        ? Math.floor(fields.production_format_version as number)
        : 1
  return { aspect_ratio: aspect, resolution, version }
}
